import {
	getMomentTz,
	useUserContext,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Alert, Button, Snackbar } from "@mui/material";
import { get } from "lodash-es";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { getShifts } from "../shared/repositories/ShiftRepository";

const moment = getMomentTz();

const SHIFT_CHECK_INTERVAL = 1000; // Check every second to keep the countdown accurate
const SHIFT_REFRESH_INTERVAL = 1800000; // Refresh shift data every 30 minutes
const SHIFT_STORAGE_KEY = "shiftAutoLogout";
const AUTO_LOGOUT_SETTING_KEY = "SYSTEM.AUTO_LOGOUT_ON_SHIFT_CHANGE";
const EARLY_WARNING_SECONDS = 600;
const FINAL_WARNING_SECONDS = 60;

const isAutoLogoutProfileCombination = (profiles: string[]) => {
	const profileNames = profiles
		.map((profile) => profile.trim())
		.filter(Boolean);

	return (
		profileNames.includes("Supervisor Profile") &&
		profileNames.every(
			(profile) =>
				profile === "Supervisor Profile" ||
				["Public Profile", "Operator Profile"].includes(profile),
		)
	);
};

const formatCountdown = (totalSeconds: number) => {
	const seconds = Math.max(0, totalSeconds);
	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = seconds % 60;

	return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
};

interface ShiftLogoutData {
	shiftEndTime: string;
	lastCheckedShiftId: number;
	userId: number | null;
}

export const AutoLogout = ({ children }: { children: ReactNode }) => {
	const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const dismissedEarlyWarningRef = useRef<string | null>(null);

	const [isEarlyWarningOpen, setIsEarlyWarningOpen] = useState(false);
	const [earlyWarningMinutes, setEarlyWarningMinutes] = useState(
		EARLY_WARNING_SECONDS / 60,
	);
	const [finalWarningSeconds, setFinalWarningSeconds] = useState<number | null>(
		null,
	);

	// Get UTL settings to check if auto-logout is enabled
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const { state } = useUTLSettingsContext();
	const isAutoLogoutEnabled = !!get(
		state,
		`settings["${AUTO_LOGOUT_SETTING_KEY}"]`,
		false,
	);

	const {
		state: { profiles, userId },
		isLoading: isUserLoading,
	} = useUserContext();
	const profilesKey = profiles.join(",");
	const isAutoLogoutActive =
		isAutoLogoutEnabled &&
		!isUserLoading &&
		isAutoLogoutProfileCombination(profiles);

	const clearWarnings = () => {
		setIsEarlyWarningOpen(false);
		setFinalWarningSeconds(null);
	};

	const fetchAndStoreShiftData = async ({ keepPendingLogout = false } = {}) => {
		// Only fetch shift data if auto-logout applies to the logged user
		if (!isAutoLogoutActive) {
			return;
		}

		if (keepPendingLogout) {
			const pendingData = localStorage.getItem(SHIFT_STORAGE_KEY);
			if (pendingData) {
				try {
					const pendingShift: ShiftLogoutData = JSON.parse(pendingData);
					const pendingLogoutTime = moment.utc(pendingShift.shiftEndTime);

					if (
						pendingShift.userId === userId &&
						moment().isBefore(pendingLogoutTime)
					) {
						return;
					}
				} catch (error) {
					console.error("Error parsing shift logout data:", error);
					localStorage.removeItem(SHIFT_STORAGE_KEY);
				}
			}
		}

		try {
			// Fetch current shift (passing null gets current shift)
			const shifts = await getShifts({
				productionDate: null,
				assetId: Number(defaultAreaId),
			});

			if (shifts && shifts.length > 0) {
				const currentShift = shifts[0];

				// Store shift end time and ID in localStorage
				const shiftData: ShiftLogoutData = {
					shiftEndTime: moment(currentShift.CurrentEnd).toISOString(),
					lastCheckedShiftId: currentShift.CurrentShiftId,
					userId,
				};

				localStorage.setItem(SHIFT_STORAGE_KEY, JSON.stringify(shiftData));
				console.log(
					`Shift auto-logout scheduled for: ${moment(
						currentShift.CurrentEnd,
					).format()}`,
				);
			}
		} catch (error) {
			console.error("Failed to fetch shift data for auto-logout:", error);
			// Retry after 5 minutes on error
			setTimeout(() => fetchAndStoreShiftData({ keepPendingLogout }), 300000);
		}
	};

	const checkShiftEnd = () => {
		// Only check shift end if auto-logout applies to the logged user
		if (!isAutoLogoutActive) {
			return;
		}

		const storedData = localStorage.getItem(SHIFT_STORAGE_KEY);

		if (storedData) {
			try {
				const shiftData: ShiftLogoutData = JSON.parse(storedData);

				// Never log out on a schedule stored by another session; the fetch replaces it
				if (shiftData.userId !== userId) {
					clearWarnings();
					return;
				}

				const logoutTime = moment.utc(shiftData.shiftEndTime);
				const now = moment();
				const secondsRemaining = logoutTime.diff(now, "seconds");

				// Check if current time has passed the shift end time
				if (secondsRemaining <= 0) {
					console.log("Shift has ended. Logging out...");

					// Clear the shift data from localStorage
					localStorage.removeItem(SHIFT_STORAGE_KEY);
					clearWarnings();

					// Clear intervals
					if (checkIntervalRef.current) {
						clearInterval(checkIntervalRef.current);
					}
					if (refreshIntervalRef.current) {
						clearInterval(refreshIntervalRef.current);
					}

					// Redirect to logout with message
					window.location.href = "/logout?message=Shift%20Change";
					return;
				}

				if (secondsRemaining <= FINAL_WARNING_SECONDS) {
					setIsEarlyWarningOpen(false);
					setFinalWarningSeconds(secondsRemaining);
					return;
				}

				setFinalWarningSeconds(null);

				const logoutKey = logoutTime.toISOString();

				if (secondsRemaining <= EARLY_WARNING_SECONDS) {
					setEarlyWarningMinutes(Math.ceil(secondsRemaining / 60));
					setIsEarlyWarningOpen(dismissedEarlyWarningRef.current !== logoutKey);
					return;
				}

				if (dismissedEarlyWarningRef.current !== logoutKey) {
					dismissedEarlyWarningRef.current = null;
				}
				setIsEarlyWarningOpen(false);
			} catch (error) {
				console.error("Error parsing shift logout data:", error);
				localStorage.removeItem(SHIFT_STORAGE_KEY);
				clearWarnings();
			}
		} else {
			clearWarnings();
		}
	};

	const handleDismissEarlyWarning = () => {
		const storedData = localStorage.getItem(SHIFT_STORAGE_KEY);

		if (storedData) {
			try {
				const shiftData: ShiftLogoutData = JSON.parse(storedData);
				dismissedEarlyWarningRef.current = moment
					.utc(shiftData.shiftEndTime)
					.toISOString();
			} catch (error) {
				console.error("Error parsing shift logout data:", error);
			}
		}

		setIsEarlyWarningOpen(false);
	};

	useEffect(() => {
		if (isAutoLogoutEnabled && isUserLoading) {
			return;
		}

		if (!isAutoLogoutActive) {
			console.log(
				isAutoLogoutEnabled
					? "Auto-logout on shift change does not apply to these profiles"
					: "Auto-logout on shift change is disabled",
			);
			// Clean up any existing data
			localStorage.removeItem(SHIFT_STORAGE_KEY);
			clearWarnings();
			return;
		}

		console.log("Auto-logout on shift change is enabled");

		// Initial fetch of shift data
		fetchAndStoreShiftData();

		// Set up interval to check shift end (and refresh the countdown)
		checkIntervalRef.current = setInterval(checkShiftEnd, SHIFT_CHECK_INTERVAL);

		// Set up interval to refresh shift data every 30 minutes
		refreshIntervalRef.current = setInterval(
			() => fetchAndStoreShiftData({ keepPendingLogout: true }),
			SHIFT_REFRESH_INTERVAL,
		);

		// Cleanup on unmount
		return () => {
			if (checkIntervalRef.current) {
				clearInterval(checkIntervalRef.current);
			}
			if (refreshIntervalRef.current) {
				clearInterval(refreshIntervalRef.current);
			}
		};
	}, [isAutoLogoutEnabled, isUserLoading, userId, profilesKey]);

	return (
		<>
			{children}
			<Snackbar
				open={isEarlyWarningOpen}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					elevation={6}
					variant="filled"
					severity="warning"
					action={
						<Button
							color="inherit"
							size="small"
							onClick={handleDismissEarlyWarning}
						>
							OK
						</Button>
					}
				>
					{`Your session will be closed in about ${earlyWarningMinutes} minutes due to the shift change.`}
				</Alert>
			</Snackbar>
			<Snackbar
				open={finalWarningSeconds !== null}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert elevation={6} variant="filled" severity="error">
					{`You will be logged out in ${formatCountdown(
						finalWarningSeconds ?? 0,
					)} due to the shift change.`}
				</Alert>
			</Snackbar>
		</>
	);
};
